import { Fragment, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

import {
  useGetCalendarManagement,
  useReserveBooking,
} from '@api/clinics/index.rq';
import { useGetProfile } from '@api/user/user.rq';
import {
  BookingReservePayload,
  ClinicProfile,
  ProfileEnum,
  UserServiceItem,
} from '@models/clinic-list';
import {
  ActiveTabEnum,
  DoctorClinicItemModel,
} from '@models/in-person-booking';
import useModal from '@hooks/useModal';
import { routeList } from '@routes/routeList';
import { getLocalStorageToken } from '@utilities/localStorageUtils';
import ActionBar from '@com/Layout/ActionBar';
import { reserveDoctorPayload } from '@models/booking';
import LoadingSpinner from '@com/_atoms/LoadingSpinner';
import NextImage from '@com/_core/NextImage';

import BookingHeader from './BookingHeader';
import DaysSlider from './DaysSlider';
import ReserveActionBar from './ReserveActionBar';
import SessionTabs from './SessionTabs';
import {
  BookingDay,
  BookingTimeSlot,
  categorizeTimeSlots,
  findFirstAvailableDay,
  getTabForStartTime,
  getTimeSlotsForDay,
  groupCalendarSlotsByDate,
} from './utils';

type ServiceBookingCalendarProps = {
  profile: ClinicProfile;
  service?: UserServiceItem;
  profileId: number;
  clinic?: DoctorClinicItemModel;
};

const getInitialTabForDay = (day: BookingDay | null): ActiveTabEnum => {
  const firstAvailable = getTimeSlotsForDay(day).find(
    (slot) => slot.isAvailable,
  );

  if (!firstAvailable) return ActiveTabEnum.MORNING;

  return getTabForStartTime(firstAvailable.startTime);
};

const getFirstAvailableSlot = (day: BookingDay | null) =>
  getTimeSlotsForDay(day).find((slot) => slot.isAvailable) ?? null;

const ServiceBookingCalendar = ({
  profile,
  service,
  profileId,
  clinic,
}: ServiceBookingCalendarProps) => {
  const { push } = useRouter();
  const { removeAllModalAction } = useModal();
  const { data: userProfileResponse } = useGetProfile();

  const queryClient = useQueryClient();

  const { data: calendar, isLoading: isCalendarLoading } =
    useGetCalendarManagement(
      {
        id: profileId,
        serviceId: service?.id,
        ...(clinic?.id && !service?.id ? { medicalCenterId: clinic.id } : {}),
        pageSize: 10,
        pageNumber: 1,
      },
      {
        enabled: true,
      },
    );

  const { mutate: reserveBooking, isPending: isReserving } =
    useReserveBooking();

  const days = useMemo(() => groupCalendarSlotsByDate(calendar), [calendar]);

  const initialDay = useMemo(
    () => findFirstAvailableDay(days),
    [days, calendar],
  );

  const [selectedDay, setSelectedDay] = useState<BookingDay | null>(initialDay);
  const [selectedTimeSlot, setSelectedTimeSlot] =
    useState<BookingTimeSlot | null>(() => getFirstAvailableSlot(initialDay));
  const [activeTab, setActiveTab] = useState<ActiveTabEnum>(() =>
    getInitialTabForDay(initialDay),
  );

  useEffect(() => {
    if (!initialDay || selectedDay) return;

    setSelectedDay(initialDay);
    setSelectedTimeSlot(getFirstAvailableSlot(initialDay));
    setActiveTab(getInitialTabForDay(initialDay));
  }, [initialDay, selectedDay]);

  const dayTimeSlots = useMemo(
    () => getTimeSlotsForDay(selectedDay),
    [selectedDay],
  );

  const categorizedSlots = useMemo(
    () => categorizeTimeSlots(dayTimeSlots),
    [dayTimeSlots],
  );

  const handleSelectDay = (day: BookingDay) => {
    setSelectedDay(day);
    setActiveTab(getInitialTabForDay(day));
    setSelectedTimeSlot(getFirstAvailableSlot(day));
  };

  const handleReserve = () => {
    if (!selectedDay || !selectedTimeSlot) {
      toast.warning('لطفا زمان مورد نظر خود را انتخاب کنید.');
      return;
    }

    const token = getLocalStorageToken();
    if (!token) {
      removeAllModalAction();
      push(routeList.loginRoute);
      return;
    }

    const patient = userProfileResponse?.queryResult?.[0];
    const patientName =
      `${patient?.firstName ?? ''} ${patient?.lastName ?? ''}`.trim();

    const createServiceName = () => {
      if (service?.serviceHierarchyName && profile?.basicInfo?.fullName) {
        return `${service?.serviceHierarchyName}، دکتر ${profile?.basicInfo?.fullName}`;
      }

      return (
        service?.serviceHierarchyName ||
        clinic?.title ||
        profile?.basicInfo?.fullName ||
        ''
      );
    };

    const toTimeWithSeconds = (time: string) => {
      const [hour = '00', minute = '00', second = '00'] = time.split(':');
      return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:${second.padStart(2, '0')}`;
    };

    if (profile?.profileType === ProfileEnum.DOCTOR && !service?.id) {
      const reserveDoctorBody: reserveDoctorPayload = {
        serviceId: profile.profileType,
        serviceName: createServiceName(),
        providerId: profile.ownerId,
        patientName,
        date: selectedDay.date,
        fromTime: toTimeWithSeconds(selectedTimeSlot.startTime),
        toTime: toTimeWithSeconds(selectedTimeSlot.endTime),
        price: service?.price ?? 0,
        discountAmount: service?.price
          ? Math.max(service?.price - service?.finalPrice, 0)
          : 0,
        discountPercentage: service?.discountPercent ?? 0,
        deposit: 0,
      };

      queryClient.setQueryData(
        ['reserve-doctor-clinic-payload'],
        reserveDoctorBody,
      );

      queryClient.setQueryData(['reserve-doctor-clinic-detail'], {
        serviceName: createServiceName(),
        date: selectedDay.date,
        fromTime: toTimeWithSeconds(selectedTimeSlot.startTime),
        toTime: toTimeWithSeconds(selectedTimeSlot.endTime),
      });

      const clinicId =
        clinic?.id ??
        selectedTimeSlot.medicalCenterId ??
        calendar?.schedules?.[0]?.medicalCenterId;

      push(
        `${routeList.doctorClinicReserve}?doctorId=${profile?.ownerId}&clinicId=${clinicId}`,
      );

      removeAllModalAction();
    } else {
      const body: BookingReservePayload = {
        ServiceId:
          profile?.profileType === ProfileEnum.DOCTOR
            ? ProfileEnum.DOCTOR
            : ProfileEnum.CLINIC,
        ServiceName: createServiceName(),
        providerId: profile.ownerId,
        patientPhoneNumber: patient?.phoneNumber ?? '',
        patientName,
        date: selectedDay.date,
        fromTime: toTimeWithSeconds(selectedTimeSlot.startTime),
        toTime: toTimeWithSeconds(selectedTimeSlot.endTime),
        price: service?.price ?? 0,
        discountAmount: service?.price
          ? Math.max(service?.price - service?.finalPrice, 0)
          : 0,
        discountPercentage: service?.discountPercent ?? 0,
        serviceIds: service?.id ? [service?.id] : [],
        deposit: service?.deposit ?? 0,
      };

      reserveBooking(body, {
        onSuccess: (orderCodeResponse) => {
          removeAllModalAction();

          push(`${routeList.clinicBooking}/${orderCodeResponse}`);
        },
        onError: (error) => {
          toast.error(error.message);
        },
      });
    }
  };

  if (isCalendarLoading || (isCalendarLoading && !initialDay))
    return (
      <div className="h-[300px] flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );

  if (!days?.length)
    return (
      <Fragment>
        <div className="h-[300px] flex flex-col items-center justify-center gap-y-3 pb-[84px]">
          <NextImage
            src="/images/inPersonEmptyState.svg"
            width={152}
            height={135}
            alt="calendar-empty-state"
          />

          <span
            className="text-Label/Small text-content-tertiary"
            data-testid="healthcare-calendar-empty"
          >
            در حال حاضر نوبتی برای این سرویس تعریف نشده است.
          </span>
        </div>

        <ActionBar type="singleAction">
          <ReserveActionBar isDisabled />
        </ActionBar>
      </Fragment>
    );

  return (
    <Fragment>
      <div
        className="flex grow flex-col gap-y-3 pb-[84px]"
        data-testid="healthcare-calendar"
      >
        <BookingHeader profile={profile} service={service} />

        <DaysSlider
          days={days}
          selectedDay={selectedDay}
          onSelectDay={handleSelectDay}
        />

        {!!days.length && (
          <SessionTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            selectedTimeSlot={selectedTimeSlot}
            categorizedSlots={categorizedSlots}
            setSelectedTimeSlot={setSelectedTimeSlot}
          />
        )}
      </div>
      <ActionBar>
        <ReserveActionBar onReserve={handleReserve} isLoading={isReserving} />
      </ActionBar>
    </Fragment>
  );
};

export default ServiceBookingCalendar;
